/**
 * Supplier claims: faulty stock the business is trying to get replaced, credited, or is giving up
 * on — plus the credit notes that come back and the per-supplier record that results.
 *
 * A claim is raised against stock already sitting in the **faulty** bucket; raising one writes no
 * stock movement at all, because the goods have not gone anywhere — they are on the premises while
 * the supplier decides. Only `resolveClaim` can move stock, and what it moves depends on the
 * outcome:
 *
 * - `replaced` — the stock returns to sale at its **original cost**. Not zero (the replacements
 *   were free, but the business paid for the originals) and not today's average.
 * - `credited` — no stock comes back; a standing credit appears against the supplier.
 * - `abandoned` — the stock is given up on and leaves the faulty bucket.
 *
 * Every route here is cost-bearing: a claim *is* a cost document, so the whole family sits behind
 * `cost.view` server-side.
 */

import { api } from '../api.js';
import {
  claimListTag,
  claimTag,
  faultyStockTag,
  movementListTag,
  stockListTag,
  supplierCreditListTag,
} from '../tags.js';
import type { Paged, ShopId, SupplierId, VariantId } from '../../types/api.js';
import type {
  Claim,
  ClaimId,
  ClaimStatus,
  SupplierCredit,
  SupplierPerformance,
} from '../../types/stockIntegrity.js';

export type ListClaimsParams = {
  readonly supplierId?: SupplierId | undefined;
  readonly shopId?: ShopId | undefined;
  readonly status?: ClaimStatus | undefined;
  readonly onlyOpen?: boolean | undefined;
  readonly page?: number | undefined;
  readonly pageSize?: number | undefined;
};

export type RaiseClaimBody = {
  readonly supplierId: SupplierId;
  readonly shopId?: ShopId | undefined;
  readonly variantId: VariantId;
  readonly inventoryUnitId?: string | undefined;
  /** Refused if it exceeds what the shop actually holds as faulty. */
  readonly quantity: number;
  /** From the supplier's invoice. The server does not derive it — cost belongs to Inventory. */
  readonly unitCostKes: number;
  readonly supplierReference?: string | undefined;
};

export const claimsApi = api.injectEndpoints({
  endpoints: (build) => ({
    listClaims: build.query<Paged<Claim>, ListClaimsParams>({
      query: (params) => ({ url: '/claims', params: { page: 1, pageSize: 25, ...params } }),
      providesTags: [claimListTag()],
    }),

    getClaim: build.query<Claim, ClaimId>({
      query: (id) => `/claims/${id}`,
      providesTags: (_result, _error, id) => [claimTag(id)],
    }),

    /** A standing balance. Credits are never netted off a purchase automatically. */
    listSupplierCredits: build.query<
      SupplierCredit[],
      { supplierId?: SupplierId | undefined; onlyUnspent?: boolean | undefined }
    >({
      query: (params) => ({ url: '/claims/credits', params }),
      providesTags: (_result, _error, { supplierId }) =>
        supplierId === undefined ? [] : [supplierCreditListTag(supplierId)],
    }),

    listSupplierPerformance: build.query<
      SupplierPerformance[],
      { supplierId?: SupplierId | undefined; from?: string | undefined; to?: string | undefined }
    >({
      query: (params) => ({ url: '/claims/performance', params }),
      providesTags: [claimListTag()],
    }),

    /** Paperwork, not a stock movement. The faulty units stay exactly where they are. */
    raiseClaim: build.mutation<ClaimId, RaiseClaimBody>({
      query: (body) => ({ url: '/claims', method: 'POST', body }),
      invalidatesTags: (_result, _error, { shopId }) => [
        claimListTag(),
        // The faulty register shows which rows already have a claim against them, so it changes
        // even though no quantity did.
        ...(shopId === undefined ? [] : [faultyStockTag(shopId)]),
      ],
    }),

    /** Only the supplier's own reference is editable, and only while the claim is open. */
    updateClaim: build.mutation<void, { id: ClaimId; supplierReference: string | null }>({
      query: ({ id, supplierReference }) => ({
        url: `/claims/${id}`,
        method: 'PUT',
        body: { supplierReference },
      }),
      invalidatesTags: (_result, _error, { id }) => [claimTag(id), claimListTag()],
    }),

    /**
     * The one call here that can move stock. `shopId` and `supplierId` are carried in the argument
     * only so the faulty register and the credit list can be invalidated — the server reads
     * neither from the body.
     */
    resolveClaim: build.mutation<
      void,
      {
        id: ClaimId;
        supplierId: SupplierId;
        shopId: ShopId;
        status: ClaimStatus;
        note?: string | undefined;
        /** Required by the server when the outcome is `credited`. */
        creditAmountKes?: number | undefined;
        /** A phone replaced by a different handset: the new one's IMEI. Empty: the same one came back. */
        replacementSerialNo?: string | undefined;
      }
    >({
      query: ({ id, status, note, creditAmountKes, replacementSerialNo }) => ({
        url: `/claims/${id}/resolution`,
        method: 'POST',
        body: { status, note, creditAmountKes, replacementSerialNo },
      }),
      invalidatesTags: (_result, _error, { id, supplierId, shopId }) => [
        claimTag(id),
        claimListTag(),
        supplierCreditListTag(supplierId),
        faultyStockTag(shopId),
        stockListTag(),
        movementListTag(),
      ],
    }),
  }),
});

export const {
  useListClaimsQuery,
  useGetClaimQuery,
  useListSupplierCreditsQuery,
  useListSupplierPerformanceQuery,
  useRaiseClaimMutation,
  useUpdateClaimMutation,
  useResolveClaimMutation,
} = claimsApi;
